import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash, X, ShoppingCart, Warning, CheckCircle, ListChecks } from '@phosphor-icons/react';

const CATEGORIES = ['Vegetables', 'Fruits', 'Dairy', 'Grains', 'Spices', 'Dry Fruits', 'Oils', 'Beverages', 'Snacks', 'Other'];

export default function GroceryPage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('pantry');
  const [pantry, setPantry] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [shoppingLists, setShoppingLists] = useState([]);
  const [showPantryForm, setShowPantryForm] = useState(false);
  const [showListForm, setShowListForm] = useState(false);
  const [pantryForm, setPantryForm] = useState({ name: '', category: 'Other', quantity: 0, unit: 'kg', min_stock: 0, price_per_unit: 0 });
  const [listForm, setListForm] = useState({ name: '', items_text: '' });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [p, ls, sl] = await Promise.all([api.get('/grocery/pantry'), api.get('/grocery/pantry/low-stock'), api.get('/grocery/shopping-lists')]);
      setPantry(p.data);
      setLowStock(ls.data);
      setShoppingLists(sl.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, [api]);

  const handleSavePantry = async (e) => {
    e.preventDefault();
    await api.post('/grocery/pantry', { ...pantryForm, quantity: Number(pantryForm.quantity), min_stock: Number(pantryForm.min_stock), price_per_unit: Number(pantryForm.price_per_unit) });
    setShowPantryForm(false);
    setPantryForm({ name: '', category: 'Other', quantity: 0, unit: 'kg', min_stock: 0, price_per_unit: 0 });
    fetchData();
  };

  const handleSaveList = async (e) => {
    e.preventDefault();
    const items = listForm.items_text.split('\n').filter(Boolean).map(line => {
      const parts = line.split(',').map(s => s.trim());
      return { name: parts[0] || '', quantity: parts[1] || '1', unit: parts[2] || 'pcs', category: parts[3] || 'Other', checked: false };
    });
    await api.post('/grocery/shopping-lists', { name: listForm.name || `Shopping ${new Date().toLocaleDateString()}`, items });
    setShowListForm(false);
    setListForm({ name: '', items_text: '' });
    fetchData();
  };

  const handleAutoList = async () => {
    try {
      const res = await api.post('/grocery/shopping-lists/from-low-stock');
      if (res.data.list_id) {
        alert('Shopping list created from low stock items!');
        fetchData();
      } else {
        alert(res.data.message || 'No low stock items');
      }
    } catch (err) { console.error(err); }
  };

  const toggleListItem = async (listId, items, idx) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], checked: !updated[idx].checked };
    await api.put(`/grocery/shopping-lists/${listId}`, { items: updated });
    fetchData();
  };

  const updatePantryQty = async (itemId, qty) => {
    await api.put(`/grocery/pantry/${itemId}`, { quantity: Math.max(0, qty) });
    fetchData();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="grocery-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Grocery Management</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Pantry & Shopping</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {lowStock.length > 0 && (
            <button onClick={handleAutoList} data-testid="auto-list-btn" className="px-4 py-2 rounded-full border border-accent text-accent text-sm font-medium hover:bg-accent/10 flex items-center gap-2">
              <Warning size={16} /> Auto-List ({lowStock.length} low)
            </button>
          )}
          {tab === 'pantry' && <button onClick={() => setShowPantryForm(true)} data-testid="add-pantry-btn" className="px-4 py-2 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2"><Plus size={16} /> Add Item</button>}
          {tab === 'lists' && <button onClick={() => setShowListForm(true)} data-testid="add-list-btn" className="px-4 py-2 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2"><Plus size={16} /> New List</button>}
        </div>
      </div>

      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit">
        {['pantry', 'lists'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`grocery-tab-${t}`} className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-card shadow-sm' : 'text-muted-foreground'}`}>
            {t === 'lists' ? 'Shopping Lists' : 'Pantry'}
          </button>
        ))}
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && tab === 'pantry' && (
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-4">
          <div className="flex items-center gap-2 mb-2"><Warning size={16} className="text-accent" /><span className="text-sm font-medium text-accent">Low Stock Alert</span></div>
          <p className="text-xs text-muted-foreground">{lowStock.map(i => i.name).join(', ')}</p>
        </div>
      )}

      {tab === 'pantry' && (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Item</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Category</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Qty</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Min</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pantry.map(item => {
                  const isLow = item.min_stock > 0 && item.quantity <= item.min_stock;
                  return (
                    <tr key={item.item_id} className={`border-b border-border last:border-0 hover:bg-muted/20 ${isLow ? 'bg-red-500/5' : ''}`} data-testid={`pantry-${item.item_id}`}>
                      <td className="px-4 py-3 font-medium">{item.name}</td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs bg-muted">{item.category}</span></td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => updatePantryQty(item.item_id, item.quantity - 1)} className="w-6 h-6 rounded bg-muted text-xs font-bold hover:bg-muted-foreground/20">-</button>
                          <span className="w-12 text-center font-medium">{item.quantity} {item.unit}</span>
                          <button onClick={() => updatePantryQty(item.item_id, item.quantity + 1)} className="w-6 h-6 rounded bg-muted text-xs font-bold hover:bg-muted-foreground/20">+</button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-muted-foreground">{item.min_stock} {item.unit}</td>
                      <td className="px-4 py-3 text-center">
                        {isLow ? <span className="text-xs text-red-500 flex items-center justify-center gap-1"><Warning size={12} /> Low</span> : <span className="text-xs text-green-600 flex items-center justify-center gap-1"><CheckCircle size={12} /> OK</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={async () => { await api.delete(`/grocery/pantry/${item.item_id}`); fetchData(); }} className="p-1 text-destructive hover:bg-destructive/10 rounded"><Trash size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pantry.length === 0 && <p className="text-center text-muted-foreground py-12">No pantry items yet. Click "Add Item" to track your kitchen inventory.</p>}
          </div>
        </div>
      )}

      {tab === 'lists' && (
        <div className="space-y-4">
          {shoppingLists.map(sl => (
            <div key={sl.list_id} className="glass-card p-5" data-testid={`shopping-list-${sl.list_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-sm">{sl.name}</h3>
                  <p className="text-xs text-muted-foreground">{sl.items?.length || 0} items - {sl.items?.filter(i => i.checked).length || 0} done</p>
                </div>
                <button onClick={async () => { await api.delete(`/grocery/shopping-lists/${sl.list_id}`); fetchData(); }} className="p-1 text-destructive"><Trash size={14} /></button>
              </div>
              <div className="space-y-1">
                {sl.items?.map((item, idx) => (
                  <button key={idx} onClick={() => toggleListItem(sl.list_id, sl.items, idx)} className={`w-full text-left flex items-center gap-2 py-1.5 px-2 rounded-lg text-sm hover:bg-muted/50 ${item.checked ? 'line-through text-muted-foreground' : ''}`}>
                    <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${item.checked ? 'bg-primary border-primary text-primary-foreground' : 'border-border'}`}>
                      {item.checked && <CheckCircle size={10} weight="bold" />}
                    </div>
                    {item.name} {item.quantity && `(${item.quantity} ${item.unit || ''})`}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {shoppingLists.length === 0 && <p className="text-center py-12 text-muted-foreground">No shopping lists yet</p>}
        </div>
      )}

      {showPantryForm && (
        <Modal title="Add Pantry Item" onClose={() => setShowPantryForm(false)}>
          <form onSubmit={handleSavePantry} className="space-y-4">
            <div><label className="overline block mb-2">Item Name</label><input type="text" value={pantryForm.name} onChange={e => setPantryForm({...pantryForm, name: e.target.value})} data-testid="pantry-name" required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30" /></div>
            <div><label className="overline block mb-2">Category</label><select value={pantryForm.category} onChange={e => setPantryForm({...pantryForm, category: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="overline block mb-2">Quantity</label><input type="number" value={pantryForm.quantity} onChange={e => setPantryForm({...pantryForm, quantity: e.target.value})} data-testid="pantry-qty" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" /></div>
              <div><label className="overline block mb-2">Unit</label><select value={pantryForm.unit} onChange={e => setPantryForm({...pantryForm, unit: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">{['kg', 'g', 'L', 'ml', 'pcs', 'packet'].map(u => <option key={u}>{u}</option>)}</select></div>
            </div>
            <div><label className="overline block mb-2">Min Stock (alert threshold)</label><input type="number" value={pantryForm.min_stock} onChange={e => setPantryForm({...pantryForm, min_stock: e.target.value})} data-testid="pantry-min" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" /></div>
            <button type="submit" data-testid="save-pantry-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Add Item</button>
          </form>
        </Modal>
      )}

      {showListForm && (
        <Modal title="New Shopping List" onClose={() => setShowListForm(false)}>
          <form onSubmit={handleSaveList} className="space-y-4">
            <div><label className="overline block mb-2">List Name</label><input type="text" value={listForm.name} onChange={e => setListForm({...listForm, name: e.target.value})} data-testid="list-name" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" placeholder="Weekly Groceries" /></div>
            <div><label className="overline block mb-2">Items (one per line: name, qty, unit)</label><textarea value={listForm.items_text} onChange={e => setListForm({...listForm, items_text: e.target.value})} rows={6} data-testid="list-items" placeholder="Rice, 5, kg&#10;Toor Dal, 2, kg&#10;Milk, 5, L" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none resize-none" /></div>
            <button type="submit" data-testid="save-list-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Create List</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) { return (<div className="fixed inset-0 z-50 flex items-center justify-center p-4"><div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} /><div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"><div className="flex items-center justify-between mb-6"><h3 className="font-heading text-xl font-bold">{title}</h3><button onClick={onClose} className="p-1 rounded-lg hover:bg-muted"><X size={20} /></button></div>{children}</div></div>); }
